import Phaser from 'phaser';
import { CONSTANTS } from '../game.js';
import { EventTypes } from '../events/EventTypes.js';

/**
 * ターゲットの基底クラス
 * アクティブターゲットと予測ターゲットの共通機能を提供
 */
export class AbstractTarget {
    /**
     * ターゲットの基底クラスのコンストラクタ
     * @param {Phaser.Scene} scene - シーンオブジェクト
     * @param {number} x - X座標
     * @param {number} y - Y座標
     */
    constructor(scene, x, y, targetType, order = 0) {
        this.scene = scene;
        this.order = order;
        
        // ターゲットの種類を設定（必須パラメータ）
        this.targetType = targetType;
        
        // 移動関連のプロパティを初期化
        this.isMoving = false;
        this.velocity = { x: 0, y: 0 };
        this.speed = CONSTANTS.MOVING_TARGET.SPEED;
        
        // 50個目以降は動くターゲットとして設定
        if (order >= CONSTANTS.MOVING_TARGET.START_COUNT) {
            this.isMoving = true;
            // 移動方向の初期化はTargetManagerが行う
            // 動き始めるときにイベントを発火
            scene.events.emit(EventTypes.TARGET.MOVEMENT_START);
        }
        
        // 的の物理オブジェクト作成
        this.body = scene.matter.add.circle(x, y, CONSTANTS.TARGET.RADIUS, {
            label: 'target',
            isStatic: true, // 的は動かない（位置は手動で更新）
            isSensor: true  // 物理的な衝突ではなくセンサーとして機能
        });
        
        this.sprite = scene.add.sprite(0, 0);
        
        // スプライトのサイズをradiusに合わせて調整
        const scale = (CONSTANTS.TARGET.RADIUS * 2) / 128; // テクスチャは128x128を想定
        this.sprite.setScale(scale);
        
        // コンテナに追加して一緒に動かせるようにする
        this.container = scene.add.container(x, y, [
            this.sprite
        ]);
        this.container.setDepth(15); // コンボ表示より前面に表示
    }
    
    
    /**
     * ターゲットのテクスチャ名を取得
     * @returns {string} テクスチャ名
     */
    getTextureNameByType() {
        // 常に黄色のターゲットテクスチャを返す
        return 'target_yellow';
    }
    
    /**
     * 物理ボディを取得
     * @returns {MatterJS.Body} 物理ボディ
     */
    getBody() {
        return this.body;
    }
    
    /**
     * 得点を取得
     * @returns {number} 得点
     */
    getPoints() {
        return this.points;
    }
    
    /**
     * 位置を取得
     * @returns {object} 位置情報 {x, y}
     */
    getPosition() {
        return this.body.position;
    }
    
    /**
     * ターゲットの種類を取得
     * @returns {string} ターゲットの種類
     */
    getTargetType() {
        return this.targetType;
    }
    
    /**
     * 位置を設定
     * @param {number} x - X座標
     * @param {number} y - Y座標
     */
    setPosition(x, y) {
        this.scene.matter.body.setPosition(this.body, { x, y });
        this.container.setPosition(x, y);
    }
    
    /**
     * 移動状態を設定
     * @param {boolean} isMoving - 移動状態
     */
    setMoving(isMoving) {
        this.isMoving = isMoving;
    }
    
    /**
     * 速度を設定
     * @param {object} velocity - 速度ベクトル {x, y}
     */
    setVelocity(velocity) {
        this.velocity = velocity;
    }

    /**
     * ターゲットの更新処理
     */
    update() {
        if (!this.isMoving) return;

        const nextX = this.body.position.x + this.velocity.x;
        const nextY = this.body.position.y + this.velocity.y;
        const radius = CONSTANTS.TARGET.RADIUS;

        // 画面端での反射チェック
        if (nextX - radius < 0 || nextX + radius > this.scene.game.config.width) {
            this.velocity.x *= -1;
        }
        if (nextY - radius < 0 || nextY + radius > this.scene.game.config.height) {
            this.velocity.y *= -1;
        }

        // 位置の更新
        this.setPosition(
            this.body.position.x + this.velocity.x,
            this.body.position.y + this.velocity.y
        );
    }

    /**
     * リソースのクリーンアップ
     */
    destroy() {
        this.scene.matter.world.remove(this.body);
        this.container.destroy();
    }

    /**
     * 移動状態を取得
     * @returns {boolean} 移動中かどうか
     */
    getIsMoving() {
        return this.isMoving;
    }

    /**
     * 順番を取得
     * @returns {number} ターゲットの順番
     */
    getOrder() {
        return this.order;
    }
}
